// components/TripBuilder.jsx
// Beautiful Trip Builder UI for multi-segment trips
// v2.6 - Fixed object rendering issues

import React, { useState } from 'react';

// Safe string helper - prevents React Error #31
const safeStr = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    // Handle common object patterns
    if (value.name) return String(value.name);
    if (value.city) return String(value.city);
    if (value.code) return String(value.code);
    if (value.label) return String(value.label);
    return '';
  }
  return String(value);
};

// Safe number helper
const safeNum = (value, defaultVal = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultVal : num;
};

// Segment Card Component
const TripSegmentCard = ({ segment, index, onSearch, isSearching, searchResults }) => {
  // Safety check
  if (!segment || typeof segment !== 'object') return null;

  const getIcon = (type) => {
    switch(type) {
      case 'flight': return '✈️';
      case 'hotel': return '🏨';
      case 'stay': return '🏠';
      case 'activity': return '🎯';
      default: return '📍';
    }
  };

  const getColor = (type) => {
    switch(type) {
      case 'flight': return '#3b82f6';
      case 'hotel': return '#8b5cf6';
      case 'stay': return '#10b981';
      case 'activity': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return safeStr(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', month: 'short', day: 'numeric' 
      });
    } catch {
      return safeStr(dateStr);
    }
  };

  const hasResults = searchResults && Array.isArray(searchResults) && searchResults.length > 0;
  const bestPrice = hasResults 
    ? Math.min(...searchResults.map(r => safeNum(r.price?.amount || r.price || r.totalPrice, 9999)))
    : null;

  // Safe segment values
  const segmentType = safeStr(segment.type) || 'flight';
  const segmentFrom = safeStr(segment.from || segment.fromCity);
  const segmentTo = safeStr(segment.to || segment.toCity);
  const segmentCity = safeStr(segment.city || segment.location);
  const segmentNights = safeNum(segment.nights, 1);
  const segmentDate = segment.date;
  const segmentEndDate = segment.endDate;
  const segmentName = safeStr(segment.name);

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '12px',
      padding: '16px 20px',
      marginBottom: '12px',
      border: '2px solid #e5e7eb',
      borderLeft: `4px solid ${getColor(segmentType)}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      transition: 'all 0.2s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        {/* Icon */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '10px',
          background: `${getColor(segmentType)}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '22px',
          flexShrink: 0
        }}>
          {getIcon(segmentType)}
        </div>
        
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '600', color: '#18181b', fontSize: '15px' }}>
            {segmentType === 'flight' && (
              <>
                {segmentFrom || 'Origin'} → {segmentTo || 'Destination'}
              </>
            )}
            {segmentType === 'hotel' && (
              <>
                {segmentCity || 'Hotel'} · {segmentNights} night{segmentNights > 1 ? 's' : ''}
              </>
            )}
            {segmentType === 'stay' && (
              <>
                {segmentCity || 'Stay'} · Staying with family/friends
              </>
            )}
            {segmentType === 'activity' && (segmentName || 'Activity')}
          </div>
          
          <div style={{ 
            fontSize: '13px', 
            color: '#71717a', 
            marginTop: '3px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>📅 {formatDate(segmentDate) || 'TBD'}</span>
            {segmentEndDate && <span>→ {formatDate(segmentEndDate)}</span>}
          </div>

          {/* Show best price if results found */}
          {hasResults && bestPrice && bestPrice < 9999 && (
            <div style={{ 
              fontSize: '13px', 
              color: '#16a34a', 
              marginTop: '4px',
              fontWeight: '500'
            }}>
              ✓ From ${bestPrice} · {searchResults.length} options found
            </div>
          )}
        </div>

        {/* Action Button */}
        {segment.needsBooking !== false && segmentType !== 'stay' ? (
          <button 
            onClick={() => onSearch && onSearch(segment, index)}
            disabled={isSearching}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: hasResults 
                ? '#dcfce7' 
                : isSearching 
                  ? '#f4f4f5' 
                  : 'linear-gradient(145deg, #fbbf24, #f59e0b)',
              color: hasResults ? '#16a34a' : isSearching ? '#a1a1aa' : '#18181b',
              fontSize: '13px',
              fontWeight: '600',
              cursor: isSearching ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
          >
            {hasResults ? '✓ Found' : isSearching ? 'Searching...' : 'Search'}
          </button>
        ) : (
          <span style={{
            padding: '8px 14px',
            borderRadius: '6px',
            background: '#f0fdf4',
            color: '#16a34a',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            No booking needed
          </span>
        )}
      </div>
    </div>
  );
};

// Main Trip Builder Component
const TripBuilder = ({ 
  tripData, 
  onSearchSegment, 
  onSearchAll,
  onAddHotel,
  onClose, 
  isSearching,
  segmentResults = {}
}) => {
  // Safety check
  if (!tripData || typeof tripData !== 'object') return null;

  const [expandedSegments, setExpandedSegments] = useState({});

  // Safe segments array
  const segments = Array.isArray(tripData.segments) ? tripData.segments : [];

  // Calculate trip summary
  const flightSegments = segments.filter(s => s && safeStr(s.type) === 'flight');
  const hotelSegments = segments.filter(s => s && safeStr(s.type) === 'hotel');
  const staySegments = segments.filter(s => s && safeStr(s.type) === 'stay');
  
  // Get unique cities for the journey line - ensure all are strings
  const cities = [];
  if (tripData.origin) {
    const originStr = safeStr(tripData.origin);
    if (originStr) cities.push(originStr);
  }
  segments.forEach(seg => {
    if (!seg) return;
    const segType = safeStr(seg.type);
    if (segType === 'flight' && seg.to) {
      const toStr = safeStr(seg.to);
      if (toStr && !cities.includes(toStr)) cities.push(toStr);
    } else if ((segType === 'hotel' || segType === 'stay') && seg.city) {
      const cityStr = safeStr(seg.city);
      if (cityStr && !cities.includes(cityStr)) cities.push(cityStr);
    }
  });

  // Safe trip data
  const tripName = safeStr(tripData.tripName) || 'Your Trip';
  const startDate = tripData.startDate;
  const endDate = tripData.endDate;
  const travelersDesc = tripData.travelers 
    ? (safeStr(tripData.travelers.description) || `${safeNum(tripData.travelers.totalCount, 1)} travelers`)
    : null;

  // Safe date formatting
  const formatTripDate = (dateStr, options) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return safeStr(dateStr);
      return date.toLocaleDateString('en-US', options);
    } catch {
      return safeStr(dateStr);
    }
  };

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '20px'
      }}>
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '28px' }}>🌴</span>
            <h2 style={{ 
              fontSize: '22px', 
              fontWeight: '700', 
              color: '#18181b',
              margin: 0
            }}>
              {tripName}
            </h2>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            fontSize: '14px', 
            color: '#71717a',
            flexWrap: 'wrap'
          }}>
            {startDate && endDate && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                📅 {formatTripDate(startDate, { month: 'short', day: 'numeric' })} 
                {' - '}
                {formatTripDate(endDate, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {travelersDesc && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                👥 {travelersDesc}
              </span>
            )}
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: '#f4f4f5',
              border: 'none',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#71717a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Journey Visualization */}
      {cities.length > 0 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginBottom: '24px',
          padding: '14px 18px',
          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
          borderRadius: '12px',
          overflowX: 'auto'
        }}>
          {cities.map((city, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && (
                <span style={{ color: '#cbd5e1', fontSize: '16px' }}>✈️</span>
              )}
              <span style={{ 
                background: idx === 0 || idx === cities.length - 1 ? '#3b82f6' : '#8b5cf6', 
                color: '#fff', 
                padding: '6px 14px', 
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                {city}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Segments */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '14px'
        }}>
          <h3 style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#71717a', 
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Trip Segments ({segments.length})
          </h3>
          
          {/* Quick Stats */}
          <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#a1a1aa' }}>
            <span>✈️ {flightSegments.length} flights</span>
            <span>🏨 {hotelSegments.length} hotels</span>
            {staySegments.length > 0 && <span>🏠 {staySegments.length} stays</span>}
          </div>
        </div>
        
        {segments.map((segment, index) => (
          <TripSegmentCard 
            key={index}
            segment={segment}
            index={index}
            onSearch={onSearchSegment}
            isSearching={isSearching}
            searchResults={segmentResults[index]}
          />
        ))}
      </div>

      {/* Add Hotel Button (if no hotels in trip) */}
      {hotelSegments.length === 0 && onAddHotel && (
        <button
          onClick={onAddHotel}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            border: '2px dashed #d1d5db',
            background: '#fafafa',
            color: '#6b7280',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: '16px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#8b5cf6';
            e.target.style.color = '#8b5cf6';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#d1d5db';
            e.target.style.color = '#6b7280';
          }}
        >
          + Add Hotels to Your Trip
        </button>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button 
          onClick={onSearchAll}
          disabled={isSearching}
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '12px',
            border: 'none',
            background: isSearching 
              ? '#f4f4f5' 
              : 'linear-gradient(145deg, #fbbf24, #f59e0b)',
            color: isSearching ? '#a1a1aa' : '#18181b',
            fontSize: '15px',
            fontWeight: '600',
            cursor: isSearching ? 'not-allowed' : 'pointer',
            boxShadow: isSearching ? 'none' : '0 2px 8px rgba(245, 158, 11, 0.3)',
            transition: 'all 0.2s ease'
          }}
        >
          {isSearching ? '🔍 Searching...' : '🔍 Search All Segments'}
        </button>
        <button style={{
          padding: '16px 24px',
          borderRadius: '12px',
          border: '2px solid #e5e7eb',
          background: '#ffffff',
          color: '#71717a',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}>
          ✏️ Edit
        </button>
      </div>

      {/* Trip Tips */}
      <div style={{
        marginTop: '20px',
        padding: '14px 16px',
        background: '#fef3c7',
        borderRadius: '10px',
        fontSize: '13px',
        color: '#92400e'
      }}>
        💡 <strong>Tip:</strong> Search all segments at once to see total trip cost, or search individually to compare options for each leg.
      </div>
    </div>
  );
};

export default TripBuilder;
